import type { Action } from '@lucidtech/las-sdk-browser';
import { createContext } from 'react';
import { createStore } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { EXPORT_TO_POWER_AUTOMATE_FUNCTION_ID, EXPORT_TO_ZAPIER_FUNCTION_ID } from '@/constants';
import { isNotConfiguredExport } from '@/utils';

export type ActionState = Partial<Action> & Pick<Action, 'config'>;

export interface TriggerProps {
  action: ActionState;
}

export interface TriggerState extends TriggerProps {
  updateAction: (updates: Partial<ActionState> | ((state: ActionState) => ActionState)) => void;
  getAction: () => ActionState;
}

export const createTriggerStore = (initProps?: Partial<TriggerProps>) => {
  const defaultProps = {
    action: { config: {} },
  };
  return createStore<TriggerState>()(
    devtools(
      immer((set, get) => ({
        ...defaultProps,
        ...initProps,
        updateAction: (updates) =>
          set((state) => {
            const v = updates instanceof Function ? updates(state.action) : updates;
            if (
              [EXPORT_TO_POWER_AUTOMATE_FUNCTION_ID, EXPORT_TO_ZAPIER_FUNCTION_ID].includes(
                state.action.functionId as string
              )
            ) {
              Object.assign(state.action, { ...v });
            } else {
              const isEnabled = !isNotConfiguredExport({ ...state.action, ...v });
              Object.assign(state.action, { ...v, enabled: isEnabled });
            }
          }),
        getAction: (): ActionState => {
          return get().action;
        },
      }))
    )
  );
};

export type TriggerStore = ReturnType<typeof createTriggerStore>;

export const TriggerContext = createContext<TriggerStore | null>(null);
